"use client";
import React, { useEffect, useRef, useState } from "react";
import { IMaskInput } from "react-imask";

type TurnstileApi = {
    render: (
        container: string | HTMLElement,
        options: {
            sitekey: string;
            callback?: (token: string) => void;
            "error-callback"?: () => void;
            "expired-callback"?: () => void;
        },
    ) => string;
    reset: (widgetId?: string) => void;
    remove?: (widgetId?: string) => void;
};

function ContactForm() {
    const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY!;

    const formRef = useRef<HTMLFormElement | null>(null);

    // Turnstile widget management
    const turnstileWidgetIdRef = useRef<string | null>(null);
    const turnstileContainerId = "contact-turnstile";

    const [isMounted, setIsMounted] = useState(false);

    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [isFocused, setIsFocused] = useState(false);

    const [mortgageReason, setMortgageReason] = useState("");
    const [propertyLocation, setPropertyLocation] = useState("");

    const [form, setForm] = useState({
        first_name: "",
        last_name: "",
        phone: "",
        email: "",
    });

    const [messageBody, setMessageBody] = useState("");

    const getTurnstile = (): TurnstileApi | undefined => {
        if (typeof window === "undefined") return undefined;
        return (window as unknown as { turnstile?: TurnstileApi }).turnstile;
    };

    const ensureTurnstileRendered = () => {
        const t = getTurnstile();
        const container = document.getElementById(turnstileContainerId);

        // If script not loaded yet or container not present
        if (!t || !container) return;

        // If we already rendered, do nothing
        if (turnstileWidgetIdRef.current) return;

        // Render once and keep the widget id
        turnstileWidgetIdRef.current = t.render(container, {
            sitekey: TURNSTILE_SITE_KEY,
            callback: () => {
                // token is stored in the hidden input automatically
                // you can log if you want:
                // console.log("Turnstile token issued");
            },
            "error-callback": () => {
                // console.log("Turnstile error");
            },
            "expired-callback": () => {
                // console.log("Turnstile expired");
            },
        });
    };

    const resetTurnstile = () => {
        const t = getTurnstile();
        const widgetId = turnstileWidgetIdRef.current;

        if (!t || !widgetId) return; // IMPORTANT: avoids "Nothing to reset found"
        t.reset(widgetId);
    };

    useEffect(() => {
        setIsMounted(true);
    }, []);

    // Render Turnstile once after mount; keep trying until API loads
    useEffect(() => {
        if (!isMounted) return;

        // Try immediately
        ensureTurnstileRendered();

        // Poll briefly until turnstile becomes available
        const interval = setInterval(() => {
            ensureTurnstileRendered();
            if (turnstileWidgetIdRef.current) clearInterval(interval);
        }, 100);

        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMounted, TURNSTILE_SITE_KEY]);

    // If component unmounts, remove widget (optional but clean)
    useEffect(() => {
        return () => {
            const t = getTurnstile();
            const widgetId = turnstileWidgetIdRef.current;
            if (t?.remove && widgetId) t.remove(widgetId);
            turnstileWidgetIdRef.current = null;
        };
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        let cleanValue = value;
        if (name === "first_name" || name === "last_name") {
            cleanValue = value.replace(/[^a-zA-Z\s]/g, "");
        } else if (name === "phone") {
            cleanValue = value.replace(/\D/g, "").slice(0, 10);
        }

        setForm((prev) => ({
            ...prev,
            [name]: cleanValue,
        }));
    };

    const getTurnstileTokenFromForm = () => {
        const formEl = formRef.current;
        if (!formEl) return "";
        return new FormData(formEl).get("cf-turnstile-response")?.toString() ?? "";
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isSubmitting) return;

        const { first_name, last_name, phone, email } = form;

        const errors: string[] = [];

        if (!messageBody) {
            errors.push("Message");
        } else if (messageBody.length > 4000) {
            setError("Message cannot exceed 4000 characters.");
            resetTurnstile();
            return;
        }

        if (!first_name) errors.push("First Name");
        if (!last_name) errors.push("Last Name");
        if (!phone && !email) errors.push("Phone or Email");
        if (!propertyLocation) errors.push("Property Location");
        if (!mortgageReason) errors.push("What you are getting in touch about");

        if (errors.length > 0) {
            setError(`Please fill in: ${errors.join(", ")}`);
            resetTurnstile();
            return;
        }

        const turnstileToken = getTurnstileTokenFromForm();

        if (!turnstileToken) {
            setError("Please complete the CAPTCHA.");
            resetTurnstile();
            return;
        }

        if (phone && phone.length !== 10) {
            setError("Phone number must be exactly 10 digits.");
            resetTurnstile();
            return;
        }

        setIsSubmitting(true);
        setError("");
        setSuccess("");

        const payload = {
            mortgageReason,
            firstName: first_name,
            lastName: last_name,
            email: email || undefined,
            phoneNumber: phone ? phone.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3") : undefined,
            propertyLocation,
            body: messageBody,
            captchaToken: turnstileToken,
        };

        try {
            const response = await fetch("https://cpl-api.blinklos.com/website/contact-us", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                // If your API returns a JSON validation payload, you can inspect it here:
                // const errBody = await response.text();
                // console.log("API error:", errBody);

                throw new Error("Failed to submit the form.");
            }

            setForm({ first_name: "", last_name: "", phone: "", email: "" });
            setMortgageReason("");
            setPropertyLocation("");
            setMessageBody("");
            setError("");
            setSuccess("Thank you! We got your application and we will reach out to you ASAP");

            resetTurnstile();
        } catch (err) {
            setError("Submission failed. Please try again.");
            setSuccess("");
            resetTurnstile();
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section className="bg-white w-[330px] sm:w-full sm:max-w-[820px] lg:max-w-[820px] m-auto pb-4 px-4 2xl:w-[820px] 2xl:max-w-none 2xl:flex 2xl:justify-center 2xl:items-center 2xl:pl-[185px] 2xl:pt-[60px]">
            <div className="max-w-3xl mx-auto font-medium 2xl:max-w-[1200px] 2xl:mx-0 2xl:ml-0 2xl:w-[780px]">
                {/* Heading */}
                <h2 className=" w-[300px] sm:w-full h-[80px] text-center text-[#212459] text-[30px] font-bold uppercase lg:text-3xl 2xl:w-[790px] 2xl:h-[40px] 2xl:text-[30px] ">
                    Contact Us
                </h2>

                <form ref={formRef} onSubmit={handleSubmit} className="2xl:w-[800px]">
                    <div className="2xl:mb-2 2xl:mt-4 2xl:w-[790px]">
                        <p
                            id="contact_about_label"
                            className="w-[300px] h-[39px] sm:w-full sm:h-auto block text-[15px] font-medium text-black mb-2 2xl:w-[402px] 2xl:h-[20px]"
                        >
                            {`FIRST, TELL US WHAT YOU'RE GETTING IN TOUCH ABOUT.`}
                        </p>
                        <div
                            role="radiogroup"
                            aria-labelledby="contact_about_label"
                            aria-required="true"
                            className="p-2 text-[13px] flex sm:flex-col flex-row pl-0 2xl:flex-row 2xl:w-[500px]"
                        >
                            <label className="inline-flex items-center gap-2 cursor-pointer relative">
                                <input
                                    type="radio"
                                    id="contact_about_buying"
                                    name="contact_about"
                                    value="BUYING"
                                    checked={mortgageReason === "Buying"}
                                    onChange={() => setMortgageReason("Buying")}
                                    className="peer appearance-none w-[22px] h-[22px] border-[3px] border-[#1275B9] rounded-full transition-all duration-200 bg-[#f2f2f2]"
                                />
                                <span className="pointer-events-none w-[10px] h-[10px] bg-[#1275B9] rounded-full scale-0 peer-checked:scale-100 absolute left-[6px] top-[6px] transition-transform duration-200"></span>
                                <span className="w-[127px] h-[20px] uppercase 2xl:w-[220px]">BUYING A HOME</span>
                            </label>
                            <label className="inline-flex items-center gap-2 cursor-pointer relative">
                                <input
                                    type="radio"
                                    id="contact_about_refinancing"
                                    name="contact_about"
                                    value="REFINANCING"
                                    checked={mortgageReason === "Refinancing"}
                                    onChange={() => setMortgageReason("Refinancing")}
                                    className="peer appearance-none w-[22px] h-[22px] border-[3px] border-[#1275B9] rounded-full transition-all duration-200 bg-[#f2f2f2]"
                                />
                                <span className="pointer-events-none w-[10px] h-[10px] bg-[#1275B9] rounded-full scale-0 peer-checked:scale-100 absolute left-[6px] top-[6px] transition-transform duration-200"></span>
                                <span className="w-[127px] h-[20px] uppercase">Refinancing</span>
                            </label>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3.5  sm:grid-cols-2 py-4 2xl:w-[790px] 2xl:gap-0">
                        <div className="2xl:w-[395px]">
                            <label htmlFor="first_name" className="sr-only">
                                First Name
                            </label>
                            <input
                                type="text"
                                id="first_name"
                                className="mt-1 rounded-md border-[3px] border-[#1275B9] bg-[#f7f7f7] p-2 w-[300px] sm:w-full h-[39px] text-[#818181] 2xl:w-[380px]"
                                name="first_name"
                                placeholder="First Name*"
                                value={form.first_name}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div className="2xl:w-[395px]">
                            <label htmlFor="last_name" className="sr-only">
                                Last Name
                            </label>
                            <input
                                type="text"
                                id="last_name"
                                className="mt-1 rounded-md border-[3px] border-[#1275B9] bg-[#f7f7f7] p-2 w-[300px] sm:w-full h-[39px] text-[#818181] 2xl:w-[380px]"
                                name="last_name"
                                placeholder="Last Name*"
                                value={form.last_name}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-2 gap-3.5 2xl:w-[790px]">
                        <div>
                            <label htmlFor="phone" className="sr-only">
                                Phone Number
                            </label>

                            <p id="phone-or-email-hint" className="sr-only">
                                Provide either a phone number or an email address.
                            </p>
                            <IMaskInput
                                id="phone"
                                aria-describedby="phone-or-email-hint"
                                mask="(000) 000-0000"
                                type="tel"
                                lazy={!isFocused}
                                value={form.phone}
                                placeholder="Phone Number"
                                unmask={false}
                                name="phone"
                                onFocus={() => setIsFocused(true)}
                                onBlur={() => {
                                    if (!form.phone) setIsFocused(false);
                                }}
                                className="mt-1 rounded-md border-[3px] border-[#1275B9] bg-[#f7f7f7] p-2 w-[300px] sm:w-full h-[39px] text-[#818181] 2xl:w-[380px]"
                                onAccept={(value: string) => {
                                    const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
                                    const syntheticEvent = {
                                        target: { name: "phone", value: digitsOnly },
                                    } as React.ChangeEvent<HTMLInputElement>;
                                    handleChange(syntheticEvent);
                                }}
                            />
                        </div>
                        <div className="2xl:w-[395px]">
                            <input
                                placeholder="Email"
                                id="email"
                                type="email"
                                aria-describedby="phone-or-email-hint"
                                className="mt-1 rounded-md  border-[3px] border-[#1275B9] bg-[#f7f7f7] p-2 w-[300px] sm:w-full h-[39px] text-[#818181] 2xl:w-[380px]"
                                name="email"
                                value={form.email}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="md:col-span-2 2xl:col-span-2 2xl:w-[774px]">
                            <select
                                id="state"
                                name="state"
                                value={propertyLocation}
                                onChange={(e) => setPropertyLocation(e.target.value)}
                                className="mt-1 rounded-md  border-[3px] border-[#1275B9] bg-[#f7f7f7] p-0 w-[300px] sm:w-full h-[39px] text-[#818181] 2xl:w-[774px] 2xl:col-span-2 "
                            >
                                <option value="" disabled hidden>
                                    State
                                </option>
                                {[
                                    "AK",
                                    "AL",
                                    "AR",
                                    "AZ",
                                    "CA",
                                    "CO",
                                    "CT",
                                    "DC",
                                    "DE",
                                    "FL",
                                    "GA",
                                    "HI",
                                    "IA",
                                    "ID",
                                    "IL",
                                    "IN",
                                    "KS",
                                    "KY",
                                    "LA",
                                    "MA",
                                    "MD",
                                    "ME",
                                    "MI",
                                    "MN",
                                    "MO",
                                    "MS",
                                    "MT",
                                    "NC",
                                    "ND",
                                    "NE",
                                    "NH",
                                    "NJ",
                                    "NM",
                                    "NV",
                                    "NY",
                                    "OH",
                                    "OK",
                                    "OR",
                                    "PA",
                                    "RI",
                                    "SC",
                                    "SD",
                                    "TN",
                                    "TX",
                                    "UT",
                                    "VA",
                                    "VT",
                                    "WA",
                                    "WI",
                                    "WV",
                                    "WY",
                                ].map((state) => (
                                    <option key={state} value={state}>
                                        {state}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="md:col-span-2 2xl:col-span-3 2xl:w-[774px]">
                            <textarea
                                id="comments"
                                name="comments"
                                placeholder="How Can We Help You?"
                                className="rounded-md border-[3px] border-[#1275B9] bg-[#f7f7f7] p-3 text-[#818181] text-[15px] resize-none w-[300px] sm:w-full 2xl:w-[774px] 2xl:col-span-2"
                                rows={2}
                                value={messageBody}
                                onChange={(e) => setMessageBody(e.target.value)}
                            />
                        </div>
                    </div>

                    {isMounted ? (
                        <div className="w-full max-w-[316px] my-4">
                            <div
                                id="contact-turnstile"
                                className="cf-turnstile"
                                data-sitekey={TURNSTILE_SITE_KEY}
                                data-action="contact-us"
                            />
                        </div>
                    ) : (
                        <div className="w-full max-w-[316px] my-4" />
                    )}

                    <div className="text-center pt-4 2xl:w-[790px]">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className={`text-[15px] w-full sm:w-auto font-bold uppercase rounded-full px-6 py-2 transition ${
                                isSubmitting
                                    ? "bg-gray-600 cursor-not-allowed"
                                    : "bg-[#1275b9] text-white hover:bg-white hover:text-[#1275b9] hover:border hover:border-[#1275b9]"
                            }`}
                        >
                            {isSubmitting ? "Submitting..." : "Submit"}
                        </button>

                        <div className="mt-4 text-center text-2.9xl" aria-live="polite" aria-atomic="true">
                            {error ? (
                                <p className="text-red-600" role="alert">
                                    {error}
                                </p>
                            ) : null}

                            {success ? (
                                <p className="text-green-600" role="status">
                                    {success}
                                </p>
                            ) : null}
                        </div>
                    </div>
                </form>
            </div>
        </section>
    );
}

export default ContactForm;
